import { useLocation } from "react-router-dom";

import { UPLOAD_STATUS } from "../constants/uploader";
import { useUploadQueue } from "../contexts/UploadQueue";
import {
  getCurrentJobForScope,
  mapJobStatusToUploadStatus,
} from "../contexts/uploadQueueReducer";

import type { UploadStatus } from "../constants/uploader";
import type { UploadJob } from "../contexts/uploadQueue.types";

export interface UseSourceMediaUploadResult {
  uploadProgress: number;
  isUploading: boolean;
  // True while the upload is queued behind another (not yet actively
  // transferring) — the panel shows "queued" instead of a 0% progress bar.
  isQueued: boolean;
  uploadStatus: UploadStatus | undefined;
  currentFile: File | null;
  // Filename as it will appear on the server (may differ from currentFile.name
  // when the server sanitizes the name, e.g. for VTT uploads via uploadStart).
  uploadedFilename: string | null;
  uploadedMimeTypeMap: Record<string, string>;
  onAddFile: (files: File[]) => void;
  onResetUpload: () => void;
  // Acknowledge the current completed upload (its source has appeared
  // server-side) so the queue can drop it. No-op if there is no completed job.
  acknowledgeUpload: () => void;
}

export interface DerivedUploadResult {
  uploadProgress: number;
  isUploading: boolean;
  isQueued: boolean;
  uploadStatus: UploadStatus | undefined;
  currentFile: File | null;
  uploadedFilename: string | null;
  uploadedMimeTypeMap: Record<string, string>;
}

export function deriveUploadResultFromJob(
  job: UploadJob | undefined,
): DerivedUploadResult {
  if (!job) {
    return {
      uploadProgress: 0,
      isUploading: false,
      isQueued: false,
      uploadStatus: undefined,
      currentFile: null,
      uploadedFilename: null,
      uploadedMimeTypeMap: {},
    };
  }

  const uploadStatus = mapJobStatusToUploadStatus(job.status);

  return {
    uploadProgress: job.progress,
    isUploading: uploadStatus === UPLOAD_STATUS.STARTED,
    isQueued: job.status === "pending",
    uploadStatus,
    currentFile: job.file,
    uploadedFilename: job.uploadedFilename,
    uploadedMimeTypeMap: { [job.displayName]: job.mimeType },
  };
}

export function useSourceMediaUpload(
  mediaId: string,
): UseSourceMediaUploadResult {
  const { jobs, enqueue, remove, acknowledgeCompleted } = useUploadQueue();
  const location = useLocation();
  const job = getCurrentJobForScope(jobs, mediaId);
  const derived = deriveUploadResultFromJob(job);

  return {
    ...derived,
    onAddFile: (files: File[]): void => {
      const file = files[0];
      if (!file) return;
      const kind = file.name.toLowerCase().endsWith(".vtt")
        ? "mediaVtt"
        : "mediaSource";

      enqueue(file, { kind, mediaId, navigatePath: location.pathname });
    },
    onResetUpload: (): void => {
      if (job) remove(job.id);
    },
    acknowledgeUpload: (): void => {
      if (job?.status === "completed") acknowledgeCompleted(job.id);
    },
  };
}
